import { ConversationProvider } from '@elevenlabs/react';
import { Loader2 } from 'lucide-react';
import { Suspense, lazy } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { AppShell } from './components/common/AppShell';
import { FEATURE_FLAGS, isFeatureEnabled } from './lib/featureFlags';
import { useAuth } from './lib/useAuth';

const Login = lazy(() => import('./pages/Login'));
const Home = lazy(() => import('./pages/Home'));
const Corpus = lazy(() => import('./pages/Corpus'));
const Dna = lazy(() => import('./pages/Dna'));
const Inbox = lazy(() => import('./pages/Inbox'));
const Job = lazy(() => import('./pages/Job'));
const Tracker = lazy(() => import('./pages/Tracker'));
const Outreach = lazy(() => import('./pages/Outreach'));
const Prep = lazy(() => import('./pages/Prep'));
const Visa = lazy(() => import('./pages/Visa'));
const Profile = lazy(() => import('./pages/Profile'));
const Presence = lazy(() => import('./pages/Presence'));
const Negotiation = lazy(() => import('./pages/Negotiation'));
const References = lazy(() => import('./pages/References'));
const Certs = lazy(() => import('./pages/Certs'));
const Settings = lazy(() => import('./pages/Settings'));
const ErrorsAdmin = lazy(() => import('./pages/ErrorsAdmin'));
const StyleGuide = lazy(() => import('./pages/StyleGuide'));
const CorpusLiveStyleGuide = lazy(() => import('./pages/CorpusLiveStyleGuide'));

export function App() {
  return (
    // The ElevenLabs ConversationProvider is hoisted here (it used to live inside
    // Corpus, behind STUDIO_V2) so EVERY route — /corpus and /dna both — has the
    // ancestor useLivingConversation needs. It is inert when no descendant calls
    // useConversation, so non-voice pages are unaffected and nothing is gated on
    // a flag at this level: the gating stays where the live surfaces mount.
    <ConversationProvider>
      <Suspense
        fallback={
          <div className="grid h-screen place-items-center text-ink-500">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        }
      >
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route
            path="*"
            element={
              <RequireAuth>
                <AppShell>
                  <Routes>
                    <Route index element={<Home />} />
                    <Route path="/corpus" element={<Corpus />} />
                    <Route path="/dna" element={<Dna />} />
                    <Route path="/inbox" element={<Inbox />} />
                    <Route path="/jobs/:jobId" element={<Job />} />
                    <Route path="/tracker" element={<Tracker />} />
                    <Route path="/outreach" element={<Outreach />} />
                    <Route path="/prep" element={<Prep />} />
                    <Route path="/visa" element={<Visa />} />
                    <Route path="/profile" element={<Profile />} />
                    <Route path="/presence" element={<Presence />} />
                    <Route path="/negotiation" element={<Negotiation />} />
                    <Route path="/references" element={<References />} />
                    <Route path="/certs" element={<Certs />} />
                    <Route path="/settings" element={<Settings />} />
                    {/* Admin-only surface. The route is always registered (no
                        flag), but the API gates every call behind requireAdmin
                        and the nav item only appears for admins, so a non-admin
                        who hits the path directly sees 403s and an error banner. */}
                    <Route path="/errors" element={<ErrorsAdmin />} />
                    <Route path="/style-guide" element={<StyleGuide />} />
                    <Route
                      path="/corpus-live"
                      element={
                        isFeatureEnabled(FEATURE_FLAGS.CORPUS_LIVE_STYLEGUIDE) ? (
                          <CorpusLiveStyleGuide />
                        ) : (
                          <Navigate to="/" replace />
                        )
                      }
                    />
                    <Route path="*" element={<Navigate to="/" replace />} />
                  </Routes>
                </AppShell>
              </RequireAuth>
            }
          />
        </Routes>
      </Suspense>
    </ConversationProvider>
  );
}

function RequireAuth({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return (
      <div className="grid h-screen place-items-center text-ink-500">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }
  if (!user) {
    const ret = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?return_to=${ret}`} replace />;
  }
  return <>{children}</>;
}
